import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  Fab,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Audit, AuditFilters } from '../../types/audit.types';
import auditService from '../../services/auditService';
import businessService from '../../services/businessService';
import formService from '../../services/formService';
import AuditCard from './components/AuditCard';
import CreateAuditDialog from './components/CreateAuditDialog';

interface Negocio {
  id: number;
  name: string;
  industry?: string;
}

interface Formulario {
  id: number;
  name: string;
}

const AuditList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  // Estado
  const [auditorias, setAudits] = useState<Audit[]>([]);
  const [negocios, setNegocios] = useState<Negocio[]>([]);
  const [formularios, setFormularios] = useState<Formulario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // Filtros — inicializados desde URL para que el botón Atrás restaure el estado
  const [filters, setFilters] = useState<AuditFilters>(() => ({
    page: parseInt(searchParams.get('page') || '1'),
    limit: isMobile ? 10 : 20,
    status: (searchParams.get('status') || undefined) as AuditFilters['status'],
    business_id: searchParams.get('business_id') ? parseInt(searchParams.get('business_id')!) : undefined,
    form_id: searchParams.get('form_id') ? parseInt(searchParams.get('form_id')!) : undefined,
  }));
  
  const [negocioSeleccionado, setNegocioSeleccionado] = useState<Negocio | null>(null);
  const [formularioSeleccionado, setFormularioSeleccionado] = useState<Formulario | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Sincronizar filtros con la URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.business_id) params.set('business_id', String(filters.business_id));
    if (filters.form_id) params.set('form_id', String(filters.form_id));
    if (filters.page && filters.page > 1) params.set('page', String(filters.page));
    setSearchParams(params, { replace: true });
  }, [filters]);

  // Cargar datos iniciales
  useEffect(() => {
    loadNegocios();
    loadFormularios();
  }, []);

  const loadNegocios = async () => {
    try {
      const result = await businessService.list({ limit: 1000 });
      const data = result.data || [];
      setNegocios(data);
      // Restaurar selección desde URL
      const urlBusinessId = parseInt(searchParams.get('business_id') || '0');
      if (urlBusinessId) {
        const found = data.find((n: Negocio) => n.id === urlBusinessId);
        if (found) setNegocioSeleccionado(found);
      }
    } catch (error) {
      console.error('Error al cargar negocios:', error);
    }
  };

  const loadFormularios = async () => {
    try {
      const result = await formService.list({ limit: 1000 });
      const data = result.data || [];
      setFormularios(data);
      // Restaurar selección desde URL
      const urlFormId = parseInt(searchParams.get('form_id') || '0');
      if (urlFormId) {
        const found = data.find((f: Formulario) => f.id === urlFormId);
        if (found) setFormularioSeleccionado(found);
      }
    } catch (error) {
      console.error('Error al cargar formularios:', error);
    }
  };

  // Cargar auditorías
  const loadAudits = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await auditService.list(filters);
      setAudits(response.data ?? []);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar auditorías');
      console.error('Error cargando auditorías:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudits();
  }, [filters]);

  // Handlers
  const handleFilterChange = (field: keyof AuditFilters, value: any) => {
    setFilters((prev: AuditFilters) => ({
      ...prev,
      [field]: value,
      page: 1, // Reset page on filter change
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev: AuditFilters) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreateSuccess = (auditoria: Audit) => {
    setCreateDialogOpen(false);
    navigate(`/audits/${auditoria.id}/execute`);
  };

  const handleCardClick = (auditoria: Audit) => {
    if ((auditoria as any).status === 'draft') {
      navigate(`/audits/${auditoria.id}/execute`);
    } else {
      navigate(`/audits/${auditoria.id}`);
    }
  };

  const handleDeleteSuccess = () => {
    loadAudits();
  };

  // Estadísticas rápidas
  const stats = {
    total: pagination.total,
    borradores: auditorias.filter(a => (a as any).status === 'draft').length,
    completadas: auditorias.filter(a => (a as any).status === 'completed').length,
    pendientes_sync: auditorias.filter(a => !(a as any).synchronized).length,
  };

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              📋 Auditorías
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestiona y ejecuta auditorías en campo (Modo: {navigator.onLine ? '🟢 Online' : '🔴 Offline'})
            </Typography>
          </Box>
          
          {/* Desktop button */}
          {!isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              size="large"
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                py: { xs: 1, sm: 1.5 },
                px: { xs: 2, sm: 3 },
              }}
            >
              Nueva Auditoría
            </Button>
          )}
        </Box>

        {/* Stats chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          <Chip label={`Total: ${stats.total}`} size="small" />
          <Chip label={`Borradores: ${stats.borradores}`} color="warning" size="small" />
          <Chip label={`Completadas: ${stats.completadas}`} color="success" size="small" />
          {stats.pendientes_sync > 0 && (
            <Chip 
              label={`⏳ Pendientes sincronización: ${stats.pendientes_sync}`} 
              color="info" 
              size="small" 
            />
          )}
        </Box>
      </Box>

      {/* Filtros */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          {/* Filtro Negocio */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              value={negocioSeleccionado}
              onChange={(_, newValue) => {
                setNegocioSeleccionado(newValue);
                handleFilterChange('business_id', newValue ? newValue.id : undefined);
              }}
              options={negocios}
              getOptionLabel={(option) => option.name}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Negocio"
                  placeholder="Buscar por negocio..."
                  size="small"
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      {option.industry && (
                        <Typography variant="caption" color="text.secondary">
                          {option.industry}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              filterOptions={(options, { inputValue }) => {
                const searchLower = inputValue.toLowerCase();
                return options.filter(
                  (option) =>
                    option.name.toLowerCase().includes(searchLower) ||
                    (option.industry && option.industry.toLowerCase().includes(searchLower))
                );
              }}
              noOptionsText="No se encontraron negocios"
              isOptionEqualToValue={(option, value) => option.id === value.id}
              ListboxProps={{
                style: { maxHeight: '300px' }
              }}
            />
          </Grid>

          {/* Filtro Formulario */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              value={formularioSeleccionado}
              onChange={(_, newValue) => {
                setFormularioSeleccionado(newValue);
                handleFilterChange('form_id', newValue ? newValue.id : undefined);
              }}
              options={formularios}
              getOptionLabel={(option) => option.name}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Formulario"
                  placeholder="Buscar por formulario..."
                  size="small"
                />
              )}
              noOptionsText="No se encontraron formularios"
              isOptionEqualToValue={(option, value) => option.id === value.id}
              ListboxProps={{
                style: { maxHeight: '300px' }
              }}
            />
          </Grid>

          {/* Filtro estado */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="estado-filter-label">Estado</InputLabel>
              <Select
                labelId="estado-filter-label"
                value={filters.status || ''}
                label="Estado"
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="draft">Borradores</MenuItem>
                <MenuItem value="completed">Completadas</MenuItem>
                <MenuItem value="in_progress">En Progreso</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Botón refrescar */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadAudits}
              disabled={loading}
              sx={{ height: '40px' }}
            >
              Refrescar
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Lista de auditorías */}
      {!loading && (
        <>
          {auditorias.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              px: 2,
              backgroundColor: theme.palette.grey[50],
              borderRadius: 2
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No se encontraron auditorías
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {negocioSeleccionado || formularioSeleccionado || filters.status
                  ? 'Prueba ajustando los filtros'
                  : 'Crea tu primera auditoría para comenzar'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  py: { xs: 0.75, sm: 1.5 },
                  px: { xs: 1.5, sm: 3 },
                }}
              >
                Nueva Auditoría
              </Button>
            </Box>
          ) : (
            <Grid container spacing={isMobile ? 2 : 3}>
              {auditorias.map((auditoria) => (
                <Grid item xs={12} sm={6} lg={4} key={auditoria.id}>
                  <AuditCard
                    auditoria={auditoria}
                    onClick={() => handleCardClick(auditoria)}
                    onDeleteSuccess={handleDeleteSuccess}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Paginación */}
          {pagination.totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 1,
              mt: 4,
              flexWrap: 'wrap'
            }}>
              <Button
                variant="outlined"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Anterior
              </Button>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Página {pagination.page} de {pagination.totalPages}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Siguiente
              </Button>
            </Box>
          )}
        </>
      )}

      {/* FAB para móvil */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setCreateDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialog crear auditoría */}
      <CreateAuditDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Container>
  );
};

export default AuditList;
